import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Wrapper, Container, Header } from '../../components';
import CurvedContainer from '../../components/CurvedContainer';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';
import SearchBar from '../../components/SearchBar';
import * as actions from './actions';
import TooltipContainer from '../../components/Tooltip';
import Sorting from '../../components/Sorting';
import Filter from '../../components/FilterSidebar';

import {
  AchievementGrid,
  AchievementContainer,
  AchievementIcon,
  AchievementLabel,
  HeaderContainer,
  ContentContainer,
  LeftContainer,
  RightContainer,
} from './styles';

const achievementImages = require.context('../../../assets/achievements');

const AchievementItem = ({ index, achievement: { icon, name, description, acquired, step } }) => {
  const [isHovered, setIsHovered] = useState(false);
  const achievementImage = achievementImages(`./${icon ? icon : 'default.png'}`);
  return (
    <AchievementContainer onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      {isHovered && <TooltipContainer width={250}>{description}</TooltipContainer>}
      <AchievementIcon image={achievementImage} acquired={acquired} />
      <AchievementLabel acquired={acquired}>{name}</AchievementLabel>
    </AchievementContainer>
  );
};

const searchQueryFilter = (searchQuery) => (object) => {
  return Object.values(object).some(
    (field) => field && String(field).toLowerCase().indexOf(searchQuery.toLowerCase()) > -1
  );
};

const acquiredFilter = (selectedFilter) => (object) => {
  if (selectedFilter) {
    if (selectedFilter === 'Acquired') {
      return object.acquired;
    } else {
      return !object.acquired;
    }
  }

  return true;
};

export default function AchievementPage() {
  const query = new URLSearchParams(window.location.search);
  const userId = query.get('id');
  const { getAchievements } = useActions(actions);
  const { achievements } = useSelector((state) => state.achievement);
  const { profile } = useSelector((state) => state.profile);

  useEffect(() => {
    if (userId) getAchievements(userId);
    else getAchievements(profile._id);
  }, [getAchievements, profile, userId]);

  const [searchQuery, setSearchQuery] = useState('');
  const [hasFilters, setHasFilters] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('Acquired');
  const [sortBy, setSortBy] = useState({ value: 'acquired', descendingOrder: false });
  const [items, setItems] = useState(achievements.slice());

  const filterOptions = [
    {
      title: 'Acquired',
      items: ['Acquired', 'Not Acquired'],
      type: 'radio',
      selectedKeyword: selectedFilter,
      getAmount: (item) => {
        return achievements.filter((i) => (item === 'Acquired' ? i.acquired : !i.acquired)).length;
      },
      onChange: (selection) => {
        setSelectedFilter(selection);
      },
    },
  ];

  const clearFilters = () => {
    setSelectedFilter('');
  };

  useEffect(() => {
    setItems(achievements.filter(searchQueryFilter(searchQuery)).filter(acquiredFilter(selectedFilter)));
    setHasFilters(selectedFilter);
  }, [searchQuery, selectedFilter, achievements]);

  const sortFunc = useCallback(
    (a, b) => {
      let desc = 1;
      let asc = -1;
      if (typeof a[sortBy.value] === 'number') {
        desc = -1;
        asc = 1;
      }
      if (a[sortBy.value] > b[sortBy.value]) {
        return sortBy.descendingOrder ? desc : asc;
      }
      if (a[sortBy.value] < b[sortBy.value]) {
        return sortBy.descendingOrder ? asc : desc;
      }
      return 0;
    },
    [sortBy]
  );

  return (
    <Wrapper color="transparent">
      <CurvedContainer bottom height={110} image={Images.tanks} />
      <Container column>
        <Header>Achievements</Header>
        <ContentContainer>
          <LeftContainer>
            <Filter items={items} hasFilters={hasFilters} clearFilters={clearFilters} options={filterOptions} />
          </LeftContainer>
          <RightContainer>
            <HeaderContainer>
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
              <Sorting
                value={sortBy.value}
                descendingOrder={sortBy.descendingOrder}
                options={[
                  { value: 'name', label: 'Name' },
                  { value: 'acquired', label: 'Acquired' },
                ]}
                onChange={(value) => setSortBy(value)}
              />
            </HeaderContainer>
            <AchievementGrid>
              {items.sort(sortFunc).map((achievement, index) => (
                <AchievementItem key={achievement.name + index} achievement={achievement} index={index} />
              ))}
            </AchievementGrid>
          </RightContainer>
        </ContentContainer>
      </Container>
    </Wrapper>
  );
}
